// Canonical JSON: the one serialisation the hash, the diffs and the client's change checks agree
// on. A leaf — no `node:crypto` — so the browser runs it too; `canonical.ts` adds the hash.
//
// Rules:
//  * object keys are emitted in sorted order (JSON object key order is not meaningful);
//  * array order is preserved (it IS meaningful — a descriptor that wants a set sorts it
//    in its own `canonicalize`);
//  * `undefined` members are dropped from objects, so "field absent" and "field present
//    but undefined" hash alike, while `null` survives because a column can hold it.

export type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };

const canonicalize = (value: unknown): unknown => {
    if (value === null || typeof value !== 'object') return value;
    if (Array.isArray(value)) {
        // JSON.stringify turns an undefined array member into null; do it here so the
        // recursion and the output agree.
        return value.map((v) => (v === undefined ? null : canonicalize(v)));
    }
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(value as Record<string, unknown>).sort()) {
        const v = (value as Record<string, unknown>)[key];
        if (v === undefined) continue;
        out[key] = canonicalize(v);
    }
    return out;
};

export const canonicalJson = (value: unknown): string => JSON.stringify(canonicalize(value));

/**
 * Structural equality under the hash's own rules (key order ignored), except that `undefined`
 * stays distinct from `null`: a field diff must tell "absent" from "present and null". A caller
 * that wants absent to equal null says so at the call site (`a ?? null`).
 */
export const sameCanonical = (a: unknown, b: unknown): boolean =>
    a === undefined || b === undefined ? a === b : canonicalJson(a) === canonicalJson(b);
